import React, { useMemo, useState } from 'react';
import {
  Datagrid,
  DateField,
  FunctionField,
  List,
  ReferenceField,
  SelectInput,
  TextField,
  useGetList,
  WrapperField,
} from 'react-admin';
import { Button, Chip, Stack, Typography } from '@mui/material';
import {
  CoronerPaymentModal,
  FuneralCoronerRow,
} from './CoronerPaymentModal';
import { CoronerPayAllModal } from './CoronerPayAllModal';

const filters = [
  <SelectInput
    source="payment_status@is"
    label="Status"
    alwaysOn
    choices={[
      { id: 'null', name: 'Unpaid' },
      { id: 'pending', name: 'Pending' },
      { id: 'paid', name: 'Paid' },
      { id: 'failed', name: 'Failed' },
    ]}
    emptyText="All"
  />,
];

export const CoronerPayments = () => (
  <List
    resource="funeral_coroners"
    filters={filters}
    filterDefaultValues={{ 'payment_status@is': 'null' }}
    sort={{ field: 'assigned_at', order: 'DESC' }}
    perPage={50}
    title="Coroner Payments"
    actions={<TopActions />}
    storeKey="coroner-payments-v1"
  >
    <Datagrid bulkActionButtons={false} rowClick={false}>
      <ReferenceField
        label="Funeral"
        source="funeral_id"
        reference="funerals"
        link="show"
      >
        <TextField source="id" />
      </ReferenceField>
      <ReferenceField
        label="Deceased"
        source="funeral_id"
        reference="funerals"
        link={false}
        sortable={false}
      >
        <ReferenceField
          source="deceased_client_id"
          reference="clients"
          link={false}
        >
          <FunctionField
            render={(c: any) =>
              c ? `${c.last_name ?? ''}, ${c.first_name ?? ''}`.replace(/^,\s*|,\s*$/g, '') : ''
            }
          />
        </ReferenceField>
      </ReferenceField>
      <TextField label="Coroner" source="coroner_name_snapshot" />
      <ReferenceField
        label="Date of death"
        source="funeral_id"
        reference="funerals"
        link={false}
        sortable={false}
      >
        <DateField source="date_time_of_death" />
      </ReferenceField>
      <FunctionField label="Status" render={(r: FuneralCoronerRow) => <StatusChip row={r} />} />
      <WrapperField label="">
        <FunctionField
          render={(r: FuneralCoronerRow) => <PayCell row={r} />}
        />
      </WrapperField>
    </Datagrid>
  </List>
);

function TopActions() {
  const { data: unpaid = [] } = useGetList<FuneralCoronerRow>('funeral_coroners', {
    filter: { 'payment_status@is': 'null' },
    pagination: { page: 1, perPage: 200 },
    sort: { field: 'assigned_at', order: 'DESC' },
  });
  const [open, setOpen] = useState(false);
  return (
    <Stack direction="row" spacing={1} sx={{ p: 1 }}>
      <Button
        variant="contained"
        size="small"
        disabled={unpaid.length === 0}
        onClick={() => setOpen(true)}
      >
        Pay all ({unpaid.length})
      </Button>
      {open && (
        <CoronerPayAllModal
          open={open}
          onClose={() => setOpen(false)}
          unpaidRows={unpaid}
        />
      )}
    </Stack>
  );
}

function PayCell({ row }: { row: FuneralCoronerRow }) {
  const [open, setOpen] = useState(false);
  if (row.payment_status === 'paid') {
    return <Typography variant="caption" color="text.secondary">—</Typography>;
  }
  return (
    <>
      <Button size="small" variant="outlined" onClick={() => setOpen(true)}>
        Pay
      </Button>
      {open && (
        <CoronerPaymentModal open={open} onClose={() => setOpen(false)} row={row} />
      )}
    </>
  );
}

function StatusChip({ row }: { row: FuneralCoronerRow }) {
  const status = row.payment_status;
  const cfg = useMemo(() => {
    switch (status) {
      case 'paid':    return { label: 'Paid',    color: 'success' as const };
      case 'pending': return { label: 'Pending', color: 'info' as const };
      case 'failed':  return { label: 'Failed',  color: 'error' as const };
      default:        return { label: 'Unpaid',  color: 'default' as const };
    }
  }, [status]);
  return <Chip size="small" label={cfg.label} color={cfg.color} variant="outlined" />;
}
